import { Error as ErrorIcon, Lock } from '@mui/icons-material';
import { Divider, Grid, Tooltip } from '@mui/material';
import { parseSecondsToHMText } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption, RoutalTooltip } from '@nx-smartmonkey/ui';
import { createRef, useEffect, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';

import { DriverColumn, driverColumnUnassigned } from '.';
import More3Points from '../../../../../assets/svg/More3Points';
import TotalDistanceIcon from '../../../../../assets/svg/Plan/DetailHeader/TotalDistanceIcon';
import TotalStopsIcon from '../../../../../assets/svg/Plan/DetailHeader/TotalStopsIcon';
import TotalTimeIcon from '../../../../../assets/svg/Plan/DetailHeader/TotalTimeIcon';
import DriverVolumeIcon from '../../../../../assets/svg/Plan/edit/DriverVolumeIcon';
import DriverWeightIcon from '../../../../../assets/svg/Plan/edit/DriverWeightIcon';
import WarningIcon from '../../../../../assets/svg/WarningIcon';
import RouteAvatar from '../../../../../components/RouteAvatar';
import { DriversActionsMenu } from '../../../../../components/menus/DriversActionsMenu';
import { Driver } from '../../../../../domain/driver/Driver';
import { Stop } from '../../../../../domain/stop/Stop';
import { useRetrieveActivePlanDrivers } from '../../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../../hooks/plan/useRetrieveActivePlanStops';
import { useSetActivePlanFocusedStop } from '../../../../../hooks/plan/useSetActivePlanFocusedStop';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import FormattedMessage from '../../../../../react-components/FormattedCustomMessage';
import Text from '../../../../../react-components/Text';
import ProblemsTooltip from '../../../shared/ProblemsTooltip';
import { StopChip } from '../../plan-stop-chip/StopChip';
import { VirtualizedRowContainer, VirtualizedStopRow } from '../../plan-stop-chip/VirtualizedStopRow';

type IDriverOption = {
  driver?: Driver;
  planDrivers: Driver[];
};

const DriverOption = ({ driver, planDrivers }: IDriverOption) => {
  if (!driver) {
    return (
      <Grid container alignItems="center">
        <WarningIcon
          color="primary"
          style={{
            marginRight: `8px`,
            height: `100%`,
          }}
        />
        <FormattedMessage id="planmap.textnotassigned" />
      </Grid>
    );
  }

  return (
    <Grid direction="row" container alignItems="center" wrap="nowrap" style={{ width: `100%` }}>
      <Grid item>
        <RouteAvatar
          driver={driver}
          planDrivers={planDrivers}
          additionalStyles={{
            marginRight: `8px`,
            display: `block`,
          }}
        />
      </Grid>

      <Grid item style={{ width: `100%`, textAlign: `justify` }}>
        <Text tagName="span" key={`typography-${driver.id}`} noWrap>
          {driver.label && driver.label.length > 20 ? (
            <RoutalTooltip
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0}
              id={driver.id}
              title={driver.label ?? ``}
              placement="top"
            >
              <Text variant="body" style={{ cursor: `pointer` }}>
                {driver.label && driver.label.length > 20 ? `${driver.label.substring(0, 20)}...` : `${driver.label}`}
              </Text>
            </RoutalTooltip>
          ) : (
            <>
              {!driver.label ? (
                <Text variant="body" style={{ cursor: `pointer`, color: RoutalPalette.secondary00 }} intlId="no_name" />
              ) : (
                <Text variant="body" style={{ cursor: `pointer` }}>
                  {driver.label && driver.label.length > 20 ? `${driver.label.substring(0, 20)}...` : `${driver.label}`}
                </Text>
              )}
            </>
          )}
        </Text>
      </Grid>

      <Grid item container justifyContent="flex-end" alignItems="center">
        <Grid item style={{ width: `18px` }}>
          {driver.is_locked && (
            <Tooltip title={<FormattedMessage id="planedit.driver.locked_tooltip" />}>
              <Lock
                style={{
                  fontSize: `18px`,
                  color: RoutalPalette.secondary60,
                }}
              />
            </Tooltip>
          )}
        </Grid>
        <Grid item style={{ width: `24px` }}>
          <ProblemsTooltip type="driver" driver={driver} tooltipPlacement="bottom">
            <ErrorIcon
              style={{
                textAlign: `center`,
                color: RoutalPalette.error.medium,
                fontSize: `24px`,
                marginTop: `3px`,
              }}
            />
          </ProblemsTooltip>
        </Grid>
      </Grid>
    </Grid>
  );
};

const DriverPanelContainer = styled(Grid)`
  height: 100%;
  width: 380px;
  border-right: 1px solid ${RoutalPalette.neutral10};
  background-color: ${RoutalPalette.neutral05};
`;

interface DriverPanelProps {
  driverColumn: DriverColumn;
  driverColumnsIds: Array<string>;
  onDriverChange: (driverId: string, index: number) => void;
}

export const DriverPanel = ({ driverColumn, driverColumnsIds, onDriverChange }: DriverPanelProps) => {
  const listRef = createRef<any>();
  const { driver_id, tab_index } = driverColumn;
  const { data: activeProject } = useRetrieveActiveProject();
  const { map: driversMap, data: drivers } = useRetrieveActivePlanDrivers();
  const { data: allStops } = useRetrieveActivePlanStops();
  const { focusedStop } = useSetActivePlanFocusedStop();

  const [anchorVehicle, setAnchorVehicle] = useState<{ screenPosition: { top: number; left: number } } | undefined>();

  const activeDriver = driver_id !== driverColumnUnassigned ? driversMap[driver_id] : undefined;

  const stops = activeDriver
    ? activeDriver.stops.sort((a, b) => (a?.order || 0) - (b?.order || 0))
    : allStops?.filter((stop) => !stop.driver_id);

  useEffect(() => {
    if (focusedStop && listRef.current) {
      const stopsId = stops?.map((stop) => stop.id);
      if (stopsId?.includes(focusedStop.id)) {
        const index = stopsId.indexOf(focusedStop.id);
        listRef.current.scrollToItem(index, `center`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedStop]);

  if (!drivers) return null;

  /**
   * Total duration is the amount of the difference between planned_end_time and planned_start_time.
   */
  const getDriverTotalDuration = (): number => {
    if (!activeDriver) return 0;

    return (activeDriver.planned_end_time ?? 0) - (activeDriver.planned_start_time ?? 0);
  };

  const getDriverTotalVolume = (): string => {
    if (!activeDriver?.max_volume || activeDriver?.max_volume === 0) return `0%`;

    const volumeAmount = activeDriver.stops.reduce(
      (p: number, n: Stop) => (n.volume && n.type === `delivery` ? p + n.volume : p),
      0
    );

    const percentage = (volumeAmount / activeDriver.max_volume) * 100;

    return `${percentage.toFixed(0)}%`;
  };

  const getDriverTotalWeight = (): string => {
    if (!activeDriver?.max_weight || activeDriver?.max_weight === 0) return `0%`;

    const volumeAmount = activeDriver.stops.reduce(
      (p: number, n: Stop) => (n.weight && n.type === `delivery` ? p + n.weight : p),
      0
    );

    const percentage = (volumeAmount / activeDriver.max_weight) * 100;

    return `${percentage.toFixed(0)}%`;
  };

  return (
    <DriverPanelContainer>
      <Grid container direction="column">
        <Grid
          item
          container
          direction="column"
          wrap="nowrap"
          style={{ height: `90px`, padding: `8px`, paddingTop: `4px`, backgroundColor: RoutalPalette.white }}
        >
          <Grid item container>
            <RoutalSelect
              id="select-driver"
              style={{ width: `100%`, backgroundColor: `white` }}
              hideSeparator
              popoverProps={{
                options: [
                  ...drivers
                    .filter((driver) => !driverColumnsIds.includes(driver.id))
                    .map(
                      (driver: Driver, idx: any): RoutalSelectMultiSelectOption => ({
                        key: idx,
                        width: 366,
                        disabled: driver.id === driver_id,
                        listItemStyles: {
                          padding: `4px 12px`,
                          paddingLeft: `13px`,
                          maxWidth: `366px`,
                        },
                        onClick: (e: any) => onDriverChange(driver.id, tab_index),
                        content: <DriverOption driver={driver} planDrivers={drivers} />,
                      })
                    ),
                  {
                    key: driverColumnUnassigned,
                    width: 366,
                    disabled: driverColumnsIds.includes(driverColumnUnassigned),
                    listItemStyles: {
                      padding: `4px 12px`,
                      paddingLeft: `13px`,
                      maxWidth: `366px`,
                    },
                    onClick: (e: any) => onDriverChange(driverColumnUnassigned, tab_index),
                    content: <DriverOption planDrivers={drivers} />,
                  },
                ],
              }}
            >
              <DriverOption driver={activeDriver} planDrivers={drivers} />
            </RoutalSelect>
          </Grid>

          {activeDriver ? (
            <Grid
              item
              container
              direction="row"
              wrap="nowrap"
              justifyContent="space-between"
              onContextMenu={(e) => {
                e.preventDefault();
                setAnchorVehicle({
                  screenPosition: {
                    top: e.clientY,
                    left: e.clientX,
                  },
                });
              }}
            >
              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <DriverWeightIcon />
                <Text style={{ whiteSpace: `nowrap` }}>{getDriverTotalWeight()}</Text>
              </Grid>

              <HSpacer medium />

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <DriverVolumeIcon />
                <Text style={{ whiteSpace: `nowrap` }}>{getDriverTotalVolume()}</Text>
              </Grid>

              <HSpacer medium />

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalDistanceIcon />
                <Text style={{ whiteSpace: `nowrap` }}>
                  {activeDriver && activeProject ? activeDriver.getTotalDistanceWithUnits(activeProject) : ``}
                </Text>
              </Grid>

              <HSpacer medium />

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalStopsIcon />
                <Text style={{ whiteSpace: `nowrap` }}>{(stops ?? []).length}</Text>
              </Grid>

              <HSpacer medium />

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalTimeIcon />
                <Text style={{ whiteSpace: `nowrap` }}>{parseSecondsToHMText(getDriverTotalDuration())}</Text>
              </Grid>

              <HSpacer medium />

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <DriversActionsMenu
                  type="popover"
                  planStops={allStops}
                  planDrivers={drivers}
                  activeProject={activeProject}
                  drivers={[activeDriver]}
                  popoverProps={{
                    hideArrow: true,
                    content: <More3Points />,
                    style: { color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
                  }}
                />
              </Grid>
            </Grid>
          ) : null}
        </Grid>
        <Divider />
      </Grid>

      <Grid container style={{ padding: `8px`, height: `calc(100% - 90px)` }}>
        <Droppable
          droppableId={driver_id}
          mode="virtual"
          renderClone={(provided, snapshot, rubric) => (
            <VirtualizedRowContainer {...provided.draggableProps} {...provided.dragHandleProps}>
              <StopChip stop={(stops || [])[rubric.source.index]} isDragging={snapshot.isDragging} />
            </VirtualizedRowContainer>
          )}
        >
          {(provided, snapshot) => (
            <>
              {(stops ?? []).length === 0 ? (
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  ref={provided.innerRef}
                  style={{
                    backgroundColor: snapshot.isDraggingOver ? RoutalPalette.primary40n : `transparent`,
                  }}
                >
                  <div
                    style={{
                      padding: `18px`,
                      height: `calc(80px - 36px)`,
                      width: `calc(100% - 40px)`,
                      border: `2px dashed ${RoutalPalette.neutral20}`,
                      borderRadius: `4px`,
                      display: `flex`,
                      justifyContent: `center`,
                      alignItems: `center`,
                    }}
                  >
                    <Text
                      intlId={`planedit.empty_placeholder.${
                        driver_id === driverColumnUnassigned ? `unassigned` : `driver`
                      }.title`}
                    />
                  </div>
                </Grid>
              ) : (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    width: `100%`,
                    height: `calc(100vh - 172px)`,
                    backgroundColor: snapshot.isDraggingOver ? RoutalPalette.primary40n : `transparent`,
                  }}
                >
                  <AutoSizer>
                    {({ height, width }: any) => {
                      return (
                        <List
                          ref={listRef}
                          height={height}
                          width={width}
                          itemCount={stops?.length || 0}
                          itemSize={64}
                          outerRef={provided.innerRef}
                          itemData={stops}
                        >
                          {VirtualizedStopRow}
                        </List>
                      );
                    }}
                  </AutoSizer>
                </div>
              )}
            </>
          )}
        </Droppable>
      </Grid>
      {anchorVehicle && driverColumn.driver_id !== `missing` && (
        <DriversActionsMenu
          type="contextual"
          setContextAnchor={setAnchorVehicle}
          planStops={allStops}
          planDrivers={drivers}
          activeProject={activeProject}
          drivers={[activeDriver!]}
          contextAnchor={anchorVehicle}
          popoverProps={{
            hideArrow: true,
            content: <More3Points />,
            style: { color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
          }}
        />
      )}
    </DriverPanelContainer>
  );
};
